// src/pages/CRM/Core/ContactsList.js

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Stack,
  Breadcrumbs,
  Link,
  Divider,
  Checkbox
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Description as FileIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../../utils/axiosInstance';
import { hasPermission } from '../../../../utils/permissionUtils';
import ContactGroupManager from './components/ContactGroupManager';
import BulkServiceReminderDialog from './components/BulkServiceReminderDialog';
import {
  ContactStatus,
  ContactStatusLabels,
  ContactStatusColors,
  LeadSource,
  LeadSourceLabels,
} from './crmEnums';

const ContactsList = () => {
  const navigate = useNavigate();
  const { chatbotId } = useParams();

  // Permission checks
  const [canView, setCanView] = useState(false);
  const [canAdd, setCanAdd] = useState(false);
  const [canEdit, setCanEdit] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // State
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: '',
    account_id: '',
  });
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    contact_type: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    job_title: '',
    contact_group_id: '',
    account_id: '',
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [accounts, setAccounts] = useState([]);

  // Group Management
  const [groups, setGroups] = useState([]);
  const [manageGroupsOpen, setManageGroupsOpen] = useState(false);

  // Selection State
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkReminderOpen, setBulkReminderOpen] = useState(false);

  // Import Dialog State
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);

  useEffect(() => {
    checkPermissions();
  }, []);

  const checkPermissions = async () => {
    const view = await hasPermission('crm', 'view');
    const add = await hasPermission('crm', 'add');
    const edit = await hasPermission('crm', 'edit');
    const del = await hasPermission('crm', 'delete');
    setCanView(view);
    setCanAdd(add);
    setCanEdit(edit);
    setCanDelete(del);
  };

  // Fetch contacts
  const fetchContacts = useCallback(async () => {
    if (!canView) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: sortBy,
        sort_order: sortOrder,
      });

      if (chatbotId) params.append('chatbot_id', chatbotId);
      if (searchQuery) params.append('search', searchQuery);
      if (filters.status) params.append('status', filters.status);
      if (filters.account_id) params.append('account_id', filters.account_id);

      const response = await axiosInstance.get(`/api/crm/contacts?${params.toString()}`);

      if (response.data.success) {
        setContacts(response.data.data || []);
        setTotalCount(response.data.pagination?.total || 0);
      } else {
        setError(response.data.message || 'Failed to fetch contacts');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch contacts');
    } finally {
      setLoading(false);
    }
  }, [canView, page, rowsPerPage, sortBy, sortOrder, searchQuery, filters, chatbotId]);

  // Fetch accounts for dropdown
  const fetchAccounts = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/accounts?per_page=100');
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch accounts:', err);
    }
  };

  const fetchGroups = async () => {
      try {
          const res = await axiosInstance.get('/api/crm/contact-groups');
          if (res.data.success) setGroups(res.data.data);
      } catch (err) { console.error(err); }
  };

  useEffect(() => {
    if (canView) {
      fetchContacts();
      fetchAccounts();
      fetchGroups();
    }
  }, [fetchContacts, canView]);

  // Handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
    setPage(0);
  };

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({ status: '', account_id: '' });
    setSearchQuery('');
    setPage(0);
  };

  const handleMenuOpen = (event, contact) => {
    setAnchorEl(event.currentTarget);
    setSelectedContact(contact);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleViewContact = () => {
    if (selectedContact) {
      navigate(`/chatbot/${chatbotId}/crm/contacts/${selectedContact.id}`);
    }
    handleMenuClose();
  };

  const handleEditContact = () => {
    if (selectedContact) {
      navigate(`/chatbot/${chatbotId}/crm/contacts/${selectedContact.id}?edit=true`);
    }
    handleMenuClose();
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedContact) return;

    try {
      const response = await axiosInstance.delete(`/api/crm/contacts/${selectedContact.id}`);

      if (response.data.success) {
        fetchContacts();
        setDeleteDialogOpen(false);
        setSelectedContact(null);
      } else {
        setError(response.data.message || 'Failed to delete contact');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete contact');
    }
  };

  // Create contact handlers
  const handleCreateDialogOpen = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      job_title: '',
      contact_group_id: '',
      account_id: '',
    });
    setFormErrors({});
    setCreateDialogOpen(true);
    fetchGroups(); // Refresh groups
  };

  const handleCreateDialogClose = () => {
    setCreateDialogOpen(false);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      job_title: '',
      account_id: '',
    });
    setFormErrors({});
  };

  const handleFormChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.first_name.trim()) {
      errors.first_name = 'First name is required';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleCreateSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const params = chatbotId ? `?chatbot_id=${chatbotId}` : '';
      
      const payload = {
        ...formData,
        account_id: formData.account_id || null
      };
      
      const response = await axiosInstance.post(`/api/crm/contacts${params}`, payload);

      if (response.data.success) {
        handleCreateDialogClose();
        fetchContacts();
      } else {
        setFormErrors({ submit: response.data.message || 'Failed to create contact' });
      }
    } catch (err) {
      setFormErrors({
        submit: err.response?.data?.message || 'Failed to create contact',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // --- IMPORT HANDLERS ---

  const handleDownloadTemplate = async () => {
    try {
      const response = await axiosInstance.get('/api/crm/contacts/template', {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'contact_import_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download template', err);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImportFile(e.target.files[0]);
      setImportResult(null); // Reset previous results
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', importFile);
    
    // Pass chatbot ID if available
    const params = chatbotId ? `?chatbot_id=${chatbotId}` : '';

    try {
      // Note: We use the raw axios instance or fetch if your wrapper doesn't handle FormData well, 
      // but usually axiosInstance works fine if Content-Type header is left to browser boundaries.
      const response = await axiosInstance.post(`/api/crm/contacts/import${params}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setImportResult({
          success: true,
          message: response.data.message,
          errors: response.data.errors || []
        });
        fetchContacts(); // Refresh list
      } else {
        setImportResult({
          success: false,
          message: response.data.message,
          errors: []
        });
      }
    } catch (err) {
      setImportResult({
        success: false,
        message: err.response?.data?.message || 'Import failed',
        errors: []
      });
    } finally {
      setUploading(false);
    }
  };

  const closeImportDialog = () => {
    setImportDialogOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  // Selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedIds(contacts.map((c) => c.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const selectedContacts = contacts.filter((c) => selectedIds.includes(c.id));

  // Helper functions
  const getFullName = (contact) => {
    return [contact.first_name, contact.last_name].filter(Boolean).join(' ') || 'Unnamed Contact';
  };

  const getInitials = (contact) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  if (!canView) {
    return (
      <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
        <Alert severity="error">You do not have permission to view contacts.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 }, mt: { xs: 2, sm: 10 } }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            component="button"
            underline="hover"
            color="inherit"
            onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}
          >
            CRM
          </Link>
          <Typography color="text.primary">Contacts</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/crm`)}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
              Contacts
            </Typography>
            <Chip label={totalCount} color="primary" size="small" />
          </Box>
          {canAdd && (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ gap: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<UploadIcon />}
                  onClick={() => setImportDialogOpen(true)}
                >
                  Import
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleCreateDialogOpen}
                >
                  Add Contact
                </Button>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search contacts..."
              value={searchQuery}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchQuery && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={handleFilterChange('status')}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {Object.values(ContactStatus).map((status) => (
                  <MenuItem key={status} value={status}>
                    {ContactStatusLabels[status]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Account</InputLabel>
              <Select
                value={filters.account_id}
                label="Account"
                onChange={handleFilterChange('account_id')}
              >
                <MenuItem value="">All Accounts</MenuItem>
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Stack direction="row" spacing={1}>
              <Tooltip title="Clear Filters">
                <IconButton onClick={clearFilters}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={fetchContacts}>
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Bulk Actions Bar */}
      {selectedIds.length > 0 && (
        <Paper sx={{ p: 1.5, mb: 1, display: 'flex', alignItems: 'center', gap: 2, bgcolor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
          <Typography variant="body2" fontWeight={600}>
            {selectedIds.length} selected
          </Typography>
          <Button
            variant="contained"
            size="small"
            startIcon={<ScheduleIcon />}
            onClick={() => setBulkReminderOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
          >
            Bulk Service Reminder
          </Button>
          <Button
            size="small"
            onClick={() => setSelectedIds([])}
            sx={{ textTransform: 'none', ml: 'auto' }}
          >
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* Contacts Table */}
      <Paper>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedIds.length > 0 && selectedIds.length < contacts.length}
                    checked={contacts.length > 0 && selectedIds.length === contacts.length}
                    onChange={handleSelectAll}
                    size="small"
                  />
                </TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : contacts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No contacts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                contacts.map((contact) => (
                  <TableRow
                    key={contact.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/chatbot/${chatbotId}/crm/contacts/${contact.id}`)}
                    selected={selectedIds.includes(contact.id)}
                  >
                    <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => handleSelectOne(contact.id)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          {getInitials(contact)}
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {getFullName(contact)}
                          </Typography>
                          {contact.job_title && (
                            <Typography variant="body2" color="text.secondary">
                              {contact.job_title}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {contact.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <EmailIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.email}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.phone && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PhoneIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.phone}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.account && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography variant="body2">{contact.account.name}</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {contact.contact_type ? (
                        <Chip
                          label={contact.contact_type === 'CUSTOMER' ? 'Customer' : 'Vendor'}
                          size="small"
                          sx={{
                            fontWeight: 600, fontSize: '0.7rem',
                            bgcolor: contact.contact_type === 'CUSTOMER' ? '#d1fae5' : '#e0e7ff',
                            color: contact.contact_type === 'CUSTOMER' ? '#059669' : '#4f46e5',
                          }}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">--</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ContactStatusLabels[contact.status]}
                        size="small"
                        color={ContactStatusColors[contact.status]}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/chatbot/${chatbotId}/crm/contacts/${contact.id}`); // or contact.id
                        }}
                      >
                       <ArrowForwardIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={handleViewContact}>
          <PersonIcon sx={{ mr: 1 }} fontSize="small" />
          View Details
        </MenuItem>
        {canEdit && (
          <MenuItem onClick={handleEditContact}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Edit
          </MenuItem>
        )}
        {canDelete && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Create Contact Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCreateDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Contact</DialogTitle>
        <DialogContent>
          {formErrors.submit && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formErrors.submit}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Contact Type</InputLabel>
                <Select
                  value={formData.contact_type}
                  label="Contact Type"
                  onChange={handleFormChange('contact_type')}
                >
                  <MenuItem value=""><em>None</em></MenuItem>
                  <MenuItem value="CUSTOMER">Customer</MenuItem>
                  <MenuItem value="VENDOR">Vendor</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.first_name}
                onChange={handleFormChange('first_name')}
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.last_name}
                onChange={handleFormChange('last_name')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={handleFormChange('email')}
                error={!!formErrors.email}
                helperText={formErrors.email}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleFormChange('phone')}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Job Title"
                value={formData.job_title}
                onChange={handleFormChange('job_title')}
              />
            </Grid>
            {/* Group Dropdown */}
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                    <FormControl fullWidth>
                        <InputLabel>Group</InputLabel>
                        <Select
                            value={formData.contact_group_id || ''}
                            label="Group"
                            onChange={(e) => handleFormChange('contact_group_id')(e)}
                        >
                            <MenuItem value=""><em>None</em></MenuItem>
                            {groups.map((g) => (
                                <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton onClick={() => setManageGroupsOpen(true)} title="Manage Groups">
                        <SettingsIcon />
                    </IconButton>
                </Box>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={formData.account_id}
                  label="Account"
                  onChange={handleFormChange('account_id')}
                >
                  <MenuItem value="">None</MenuItem>
                  {accounts.map((account) => (
                    <MenuItem key={account.id} value={account.id}>
                      {account.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCreateDialogClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={24} /> : 'Create Contact'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Contact</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{selectedContact && getFullName(selectedContact)}</strong>?
            This action will archive the contact.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* IMPORT DIALOG */}
      <Dialog open={importDialogOpen} onClose={closeImportDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Import Contacts</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1 }}>
            <Typography variant="body2" color="text.secondary" paragraph>
              Download the template to ensure your CSV file has the correct headers.
            </Typography>
            <Button 
                startIcon={<DownloadIcon />} 
                onClick={handleDownloadTemplate}
                size="small"
                variant="outlined"
            >
                Download CSV Template
            </Button>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center', p: 3, border: '2px dashed #ccc', borderRadius: 2 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="raised-button-file"
              type="file"
              onChange={handleFileChange}
            />
            <label htmlFor="raised-button-file">
              <Button variant="contained" component="span" startIcon={<UploadIcon />}>
                Select CSV File
              </Button>
            </label>
            {importFile && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                    <FileIcon color="action" />
                    <Typography variant="body1">{importFile.name}</Typography>
                </Box>
            )}
          </Box>

          {/* Import Results Feedback */}
          {importResult && (
            <Box sx={{ mt: 3 }}>
                <Alert severity={importResult.success ? (importResult.errors.length > 0 ? "warning" : "success") : "error"}>
                    {importResult.message}
                </Alert>
                {importResult.errors.length > 0 && (
                    <Paper variant="outlined" sx={{ mt: 2, p: 1, maxHeight: 150, overflow: 'auto', bgcolor: '#fff3e0' }}>
                        <Typography variant="caption" fontWeight="bold">Skipped Rows:</Typography>
                        <ul style={{ margin: '5px 0', paddingLeft: 20 }}>
                            {importResult.errors.map((err, idx) => (
                                <li key={idx}>
                                    <Typography variant="caption" color="error">{err}</Typography>
                                </li>
                            ))}
                        </ul>
                    </Paper>
                )}
            </Box>
          )}

        </DialogContent>
        <DialogActions>
          <Button onClick={closeImportDialog}>Close</Button>
          <Button 
            onClick={handleImportSubmit} 
            variant="contained" 
            disabled={!importFile || uploading}
          >
            {uploading ? <CircularProgress size={24} /> : 'Upload & Import'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Group Manager - NOW INSIDE RETURN */}
      <ContactGroupManager
          open={manageGroupsOpen}
          onClose={() => setManageGroupsOpen(false)}
          onChange={fetchGroups}
      />

      {/* Bulk Service Reminder Dialog */}
      <BulkServiceReminderDialog
        open={bulkReminderOpen}
        onClose={(created) => {
          setBulkReminderOpen(false);
          if (created) setSelectedIds([]);
        }}
        selectedContacts={selectedContacts}
        chatbotId={chatbotId}
      />
    </Box>
  );
};

export default ContactsList;